import { sortWorkstations } from "~/features/workstationConfig/sortWorkstations";
import {
  AutostorePortDto,
  CreateWorkstation,
  UpdateWorkstation,
  WorkstationSummaryDto,
  WorkstationResponseDto,
  ConfigPortsForWorkstationModeRequest,
  GetWorkstationInductionConfigurationResponse,
  GetWorkstationPickingConfigurationResponse,
  PostWorkstationInductionConfigurationRequest,
  PostWorkstationPickingConfigurationRequest
} from "~/types/api";

import { warehouseApi } from "./warehouseApi";

export type GetWorkstationsForFcIdParams = {
  fulfillmentCenterId: string;
};

export const workstationApi = warehouseApi.injectEndpoints({
  endpoints: (build) => ({
    getWorkstations: build.query<WorkstationSummaryDto[], void>({
      query: () => ({
        url: `/workstations`,
        method: "GET"
      }),
      transformResponse: (response: WorkstationSummaryDto[]) => {
        return sortWorkstations(response);
      },
      providesTags: ["workstation"]
    }),
    getWorkstationsForFcId: build.query<
      WorkstationSummaryDto[],
      GetWorkstationsForFcIdParams
    >({
      query: ({ fulfillmentCenterId }) => ({
        url: `/workstations`,
        method: "GET",
        params: { fulfillmentCenterId }
      }),
      transformResponse: (response: WorkstationSummaryDto[]) => {
        return sortWorkstations(response);
      },
      providesTags: ["workstation"]
    }),
    deleteWorkstation: build.mutation<
      void,
      {
        workstationId: Guid;
      }
    >({
      query: ({ workstationId }) => ({
        url: `/workstations/${workstationId}`,
        method: "DELETE"
      }),
      invalidatesTags: ["workstation"]
    }),
    putWorkstation: build.mutation<void, UpdateWorkstation>({
      query: (workstation) => ({
        url: `/workstations/V2`,
        method: "PUT",
        body: workstation
      }),
      invalidatesTags: ["workstation"]
    }),
    postDeactivateWorkstation: build.mutation<void, string>({
      query: (workstationId) => ({
        url: `/workstations/${workstationId}/deactivate-workstation`,
        method: "POST"
      }),
      invalidatesTags: ["workstation"]
    }),
    postActivateWorkstation: build.mutation<void, string>({
      query: (workstationId) => ({
        url: `/workstations/${workstationId}/activate-workstation`,
        method: "POST"
      }),
      invalidatesTags: ["workstation"]
    }),
    postCreateWorkstation: build.mutation<
      WorkstationResponseDto,
      CreateWorkstation
    >({
      query: (workstation) => ({
        url: `/workstations/V2`,
        method: "POST",
        body: workstation
      }),
      invalidatesTags: ["workstation"]
    }),
    getMultiports: build.query<AutostorePortDto[], string>({
      query: (autostoreGridId) => ({
        url: `/autostore-grid/${autostoreGridId}/get-multi-ports`,
        method: "GET"
      }),
      providesTags: ["port"]
    }),
    getUnassignedPorts: build.query<number[], string>({
      query: (autostoreGridId) => ({
        url: `/autostore-grid/${autostoreGridId}/get-unassigned-ports`,
        method: "GET"
      }),
      providesTags: ["workstation", "autostore grid"]
    }),
    configurePorts: build.mutation<
      void,
      {
        autostoreGridId: Guid;
        workstationId: Guid;
        body: ConfigPortsForWorkstationModeRequest;
      }
    >({
      query: ({ autostoreGridId, workstationId, body }) => ({
        url: `/autostore-grid/${autostoreGridId}/workstation/${workstationId}/configure-ports`,
        method: "POST",
        body
      }),
      invalidatesTags: (_, error) => (error ? [] : ["workstation"])
    }),
    startInduction: build.mutation<
      void,
      {
        autostoreGridId: Guid;
        workstationId: Guid;
        forcedPortsForWholeBins?: number[];
      }
    >({
      query: ({ autostoreGridId, workstationId, forcedPortsForWholeBins }) => ({
        url: `/workstations/grid/${autostoreGridId}/workstation/${workstationId}/start-induction`,
        method: "POST",
        body: { forcedPortsForWholeBins }
      })
    }),
    startBinReconfiguration: build.mutation<
      void,
      { workstationId: Guid; binConfigurationId: Guid }
    >({
      query: ({ workstationId, binConfigurationId }) => ({
        url: `/workstations/${workstationId}/start-bin-reconfiguration`,
        method: "POST",
        body: { binConfigurationId }
      })
    }),
    confirmBinReconfiguration: build.mutation<
      void,
      {
        workstationId: Guid;
        sourceBinConfigurationId: Guid;
        targetBinConfigurationId: Guid;
        binNumber: number;
      }
    >({
      query: ({ workstationId, ...rest }) => ({
        url: `/workstations/${workstationId}/confirm-bin-reconfiguration`,
        method: "POST",
        body: rest
      }),
      invalidatesTags: ["empty bin report", "autostore bin", "port status"]
    }),
    closeWorkstation: build.mutation<
      void,
      { autostoreGridId: Guid; workstationId: Guid }
    >({
      query: ({ autostoreGridId, workstationId }) => ({
        url: `/workstations/grid/${autostoreGridId}/workstation/${workstationId}/close-workstation`,
        method: "POST"
      })
    }),
    getWorkstationInductionConfiguration: build.query<
      GetWorkstationInductionConfigurationResponse,
      { autostoreGridId: Guid; workstationId: Guid }
    >({
      query: ({ autostoreGridId, workstationId }) => ({
        url: `/autostore-grid/${autostoreGridId}/workstation-induction-configurations/${workstationId}`,
        method: "GET"
      }),
      providesTags: (_result, _error, { workstationId }) => [
        { type: "workstation induction configuration", id: workstationId }
      ]
    }),
    postWorkstationInductionConfiguration: build.mutation<
      void,
      {
        autostoreGridId: Guid;
        workstationId: Guid;
        body: PostWorkstationInductionConfigurationRequest;
      }
    >({
      query: ({ autostoreGridId, workstationId, body }) => ({
        url: `/autostore-grid/${autostoreGridId}/workstation-induction-configurations/${workstationId}`,
        method: "POST",
        body
      }),
      invalidatesTags: (_result, error, { workstationId }) =>
        error
          ? []
          : [
              {
                type: "workstation induction configuration",
                id: workstationId
              }
            ]
    }),
    getWorkstationPickingConfiguration: build.query<
      GetWorkstationPickingConfigurationResponse,
      { autostoreGridId: Guid; workstationId: Guid }
    >({
      query: ({ autostoreGridId, workstationId }) => ({
        url: `/autostore-grid/${autostoreGridId}/workstation-picking-configurations/${workstationId}`,
        method: "GET"
      }),
      providesTags: (_result, _error, { workstationId }) => [
        { type: "workstation picking configuration", id: workstationId }
      ]
    }),
    postWorkstationPickingConfiguration: build.mutation<
      void,
      {
        autostoreGridId: Guid;
        workstationId: Guid;
        body: PostWorkstationPickingConfigurationRequest;
      }
    >({
      query: ({ autostoreGridId, workstationId, body }) => ({
        url: `/autostore-grid/${autostoreGridId}/workstation-picking-configurations/${workstationId}`,
        method: "POST",
        body
      }),
      invalidatesTags: (_result, error, { workstationId }) =>
        error
          ? []
          : [{ type: "workstation picking configuration", id: workstationId }]
    })
  })
});
